import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { 
  Users, 
  Building2, 
  ArrowRight, 
  CheckCircle2,
  Sparkles,
  Clock
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import axios from "axios";
import { toast } from "sonner";

const API = `${window.location.origin}/api`;

export default function TryNowSection() {
  const [freeInstanceModal, setFreeInstanceModal] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
    phone: ""
  });
  const [loading, setLoading] = useState(false);

  const handleFreeInstanceSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await axios.post(`${API}/demo-request`, {
        ...formData,
        message: "Free 30-day instance request - up to 9 users"
      });
      toast.success("Request submitted! We'll set up your instance and reach out shortly.");
      setFormData({ name: "", email: "", company: "", phone: "" });
      setFreeInstanceModal(false);
    } catch (error) {
      toast.error("Failed to submit request. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="section-padding bg-white" data-testid="try-now-section">
      <div className="container-main">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-green-50 border border-green-100 mb-6">
            <Sparkles size={16} className="text-green-600" />
            <span className="text-sm font-medium text-green-700">Experience KineServe Today</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold tracking-tight text-slate-900 mb-6">
            Ready to see it in action?
          </h2>
          <p className="text-lg text-slate-600 leading-relaxed">
            No lengthy sales calls. No complicated setup. Choose how you want to explore KineServe.
          </p>
        </div>

        {/* Two Options */}
        <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
          {/* Option 1: Explore Live Demo */}
          <div className="border border-slate-200 p-8 md:p-10 hover:border-slate-300 hover:shadow-lg transition-all group">
            <div className="w-14 h-14 bg-blue-50 flex items-center justify-center mb-6 group-hover:bg-blue-100 transition-colors">
              <Users size={28} className="text-blue-600" />
            </div>
            
            <h3 className="text-2xl font-bold text-slate-900 mb-3">
              Explore Our Live Environment
            </h3>
            
            <p className="text-slate-600 mb-6 leading-relaxed">
              Jump into Grey Bull Technologies' live KineServe instance as an end user. 
              Browse the service catalog, raise a ticket, and experience the platform firsthand. 
              Need more access? Request a support agent role.
            </p>

            <ul className="space-y-3 mb-8">
              {[
                "Instant access — no signup required",
                "Real environment with sample data",
                "Request elevated access anytime"
              ].map((item, i) => (
                <li key={i} className="flex items-center gap-3 text-slate-700">
                  <CheckCircle2 size={18} className="text-green-500 flex-shrink-0" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>

            <a 
              href="https://kineserve.in" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex"
            >
              <Button 
                className="bg-slate-900 text-white hover:bg-slate-800 px-8 py-6 font-medium btn-lift w-full md:w-auto"
                data-testid="explore-demo-btn"
              >
                Explore Live Demo
                <ArrowRight size={18} className="ml-2" />
              </Button>
            </a>
          </div>

          {/* Option 2: Get Free Instance */}
          <div className="border-2 border-blue-500 p-8 md:p-10 relative bg-gradient-to-br from-blue-50/50 to-white">
            {/* Popular Badge */}
            <div className="absolute -top-4 left-8 bg-blue-600 text-white px-4 py-1.5 text-sm font-medium">
              Most Popular
            </div>
            
            <div className="w-14 h-14 bg-blue-100 flex items-center justify-center mb-6">
              <Building2 size={28} className="text-blue-600" />
            </div>
            
            <h3 className="text-2xl font-bold text-slate-900 mb-3">
              Launch Your Own Instance
            </h3>
            
            <p className="text-slate-600 mb-6 leading-relaxed">
              Get a dedicated KineServe environment for your team — completely free for 30 days. 
              Invite up to 9 users, configure your workflows, and see how KineServe transforms 
              your IT operations.
            </p>

            <ul className="space-y-3 mb-8">
              {[
                "Your own subdomain (yourcompany.kineserve.com)",
                "Up to 9 support agents included",
                "Full feature access — no restrictions"
              ].map((item, i) => (
                <li key={i} className="flex items-center gap-3 text-slate-700">
                  <CheckCircle2 size={18} className="text-green-500 flex-shrink-0" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>

            <div className="flex items-center gap-3 mb-6 p-3 bg-amber-50 border border-amber-200">
              <Clock size={20} className="text-amber-600 flex-shrink-0" />
              <span className="text-sm text-amber-800 font-medium">
                First month free — no credit card required
              </span>
            </div>

            <Button 
              onClick={() => setFreeInstanceModal(true)}
              className="bg-blue-600 text-white hover:bg-blue-700 px-8 py-6 font-medium btn-lift w-full md:w-auto"
              data-testid="get-free-instance-btn"
            >
              Get Your Free Instance
              <ArrowRight size={18} className="ml-2" />
            </Button>
          </div>
        </div>

        {/* Bottom note */}
        <p className="text-center text-slate-500 mt-12 text-sm">
          Questions? Reach out at{" "}
          <a href="mailto:info@greybulltechnologies.com" className="text-blue-600 hover:underline">
            info@greybulltechnologies.com
          </a>
        </p>
      </div>

      {/* Free Instance Modal */}
      <Dialog open={freeInstanceModal} onOpenChange={setFreeInstanceModal}>
        <DialogContent className="sm:max-w-[500px] bg-white border-slate-200 shadow-2xl" data-testid="free-instance-modal">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-slate-900">Get Your Free Instance</DialogTitle>
            <p className="text-slate-600 mt-2">
              30 days free. Up to 9 users. Your own subdomain.
            </p>
          </DialogHeader>

          <form onSubmit={handleFreeInstanceSubmit} className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  Your Name *
                </label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder="John Doe"
                  required
                  className="bg-white"
                  data-testid="instance-name-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  Work Email *
                </label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  placeholder="john@company.com"
                  required
                  className="bg-white"
                  data-testid="instance-email-input"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">
                Company / Organization *
              </label>
              <Input
                value={formData.company}
                onChange={(e) => setFormData({...formData, company: e.target.value})}
                placeholder="Your Company Name"
                required
                className="bg-white"
                data-testid="instance-company-input"
              />
              <p className="text-xs text-slate-500 mt-1">
                This will be used for your subdomain (e.g., yourcompany.kineserve.com)
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">
                Phone (Optional)
              </label>
              <Input
                value={formData.phone}
                onChange={(e) => setFormData({...formData, phone: e.target.value})}
                placeholder="+91 123 456 7890"
                className="bg-white"
                data-testid="instance-phone-input"
              />
            </div>

            <div className="bg-slate-50 p-4 space-y-2 rounded-lg">
              <p className="text-sm font-medium text-slate-900">What you'll get:</p>
              <ul className="text-sm text-slate-600 space-y-1">
                <li>• Dedicated instance at yourcompany.kineserve.com</li>
                <li>• Full admin access to configure workflows</li>
                <li>• Up to 9 support agent licenses</li>
                <li>• 30 days free — no strings attached</li>
              </ul>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-blue-600 text-white hover:bg-blue-700 py-4 font-medium btn-lift"
              data-testid="submit-instance-request-btn"
            >
              {loading ? "Submitting..." : "Request My Free Instance"}
            </Button>

            <p className="text-xs text-slate-500 text-center">
              We'll set up your instance within 24 hours and send you the login details.
            </p>
          </form>
        </DialogContent>
      </Dialog>
    </section>
  );
}
